import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CheckCircle2,
  Shell,
  LeafyGreen,
  ZapOff,
} from "lucide-react";
import ozone  from '@/assets/fresh-gear-ozone.jpg'

const FreashGearPage = () => {
  const values = [
    {
      icon: LeafyGreen,
      title: "Environmentally Friendly",
      description: "Oxidizes germs it comes in contact with and is one of the most environmentally friendly disinfecting methods.",
    },
    {
      icon: Shell,
      title: "Kills Germs",
      description: "Kills up to 99.99% of germs in both interior and exterior of sport gears and Personal Protective Equipments.",
    },
    {
      icon: ZapOff,
      title: "Removes Odors",
      description: "Removes nasty odors naturally and doesn't just mask them with sprays or fragrance.",
    },
  ];

  return (
    <div className="pt-20">

      {/* Fresh Gear Section */}
      <section className="bg-gradient-to-br from-white via-sky-50 to-sky-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-sky-600 font-medium text-sm uppercase tracking-wider block text-center">Fresh Gear Cyclone</span>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>              
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
                Fresh Gear Ozone Sanitization
              </h2>
              <p className="text-slate-600 mt-6 text-lg">
                Our Fresh Gear Cyclone is designed to keep all kinds of equipment smelling fresh and clean.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Ice hockey kits & sports gear",
                  "Leather jackets, bags & shoes",
                  "Chemical-free, environmentally friendly",
                  "Dry-process ozone sanitization",
                  "Eliminates odors completely",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-sky-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-sky-600 hover:bg-sky-700 rounded-full px-8" data-testid="freshgear-book-btn">
                    Book Fresh Gear Service
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src={ozone}
                alt="Fresh Gear Ozone"
                className="rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">Protect The Gear</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              Fresh Gear Advantages 
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-slate-100 service-card" data-testid={`value-${index}`}>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hero */}
      <section className="bg-gradient-to-br from-white via-sky-50 to-sky-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="griditems-center">
            <div className="text-center">
              <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">How It Works</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mt-3">
                Ozone Sanitizing Machine
              </h1>
            </div>
            <div className="text-justify grid lg:grid-cols-2 gap-12">
              <div>                  
                <p className="text-slate-600 mt-6 text-lg leading-relaxed">
                  The Fresh Gear Cyclone is easy to use, with a simple one-touch control panel that allows you to choose from three different cycle times. 
                  Its large capacity can accommodate up to four sets of gear at once, making it ideal for families, sports teams, and other organizations. 
                  With its advanced features and benefits, the Fresh Gear Cyclone is the perfect solution for anyone looking to keep their sports gear and equipment 
                  clean and fresh-smelling.
                </p>
              </div>
              <div>
                <p className="text-slate-600 mt-6 text-lg leading-relaxed">
                  The Fresh Gear Cyclone is a powerful and efficient odor elimination system designed to keep all kinds of ice hockey kits, leather jackets, 
                  leather bags, lather shows, and other equipment smelling fresh and clean. This innovative system uses a combination of cyclonic action and 
                  UV-C light to eliminate odor-causing bacteria, viruses, and fungi from your gear.
                </p>
              </div>
            </div> 
            <div className="mt-12 text-left relative">
              <span className="text-sky-600 font-medium text-lg uppercase tracking-wider">What is Ozone?</span>
              <iframe
                src="https://youtube.com/embed/2h3HUMcerQE"
                alt="Fresh Gear Ozone"
                className="rounded-2xl shadow-lg min-h-24 md:min-h-80 max-w-64 md:max-w-[100%] md:min-w-[60%]"
                allow="autoplay; accelerometer; allowFullScreen"
              />
            </div>
          </div>
        </div>
      </section>     


      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-600 to-sky-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Visit Us Today
          </h2>
          <p className="text-sky-100 mt-4 text-lg">
            Open 6am - 11pm daily
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/booking">
              <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8" data-testid="about-cta-book-btn">
                Book a Pickup
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8" data-testid="about-cta-contact-btn">
                Get Directions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreashGearPage;
